/** Effective qty for quote UI / summaries (matches editor & list tables). */
export function effectiveQuoteLineQty(it) {
  const q = Number(it?.qty);
  if (Number.isFinite(q) && q !== 0) return q;
  const L = Number(it?.length) || 0;
  const H = Number(it?.height) || 0;
  return L * H;
}

export function quoteLineBase(it) {
  return effectiveQuoteLineQty(it) * (Number(it?.rate) || 0);
}

export function quoteLineDiscountAmount(it) {
  const base = quoteLineBase(it);
  const d = Number(it?.discount) || 0;
  const discountType = String(it?.discountType || '').trim();
  if (
    discountType === '\u20b9' ||
    discountType === '₹' ||
    discountType === 'Rs' ||
    discountType === 'INR' ||
    discountType === 'â‚¹'
  ) return Math.min(base, d);
  return (base * d) / 100;
}

export function quoteLineNetBeforeGst(it) {
  return Math.max(0, quoteLineBase(it) - quoteLineDiscountAmount(it));
}

export function isQuoteAddonLine(it) {
  const section = String(it?.space || it?.section || it?.Space || '').trim();
  return it?._isAddon === true || it?.isAddon === true || /^electrical work$/i.test(section);
}

export function isQuoteLineEnabled(it) {
  if (!isQuoteAddonLine(it)) return true;
  const value = it?._addonEnabled ?? it?.addonEnabled;
  if (value === false) return false;
  if (String(value).trim().toLowerCase() === 'false') return false;
  return true;
}

export function activeQuoteItems(items = []) {
  return (items || []).filter(isQuoteLineEnabled);
}

export function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

/** Adjustment from saved charge rows; itemsSub is sum of line nets before GST (same as quote subTotal when gst=0). */
export function quoteChargesAdjustment(charges, itemsSubPreGst) {
  const base = Number(itemsSubPreGst) || 0;
  let x = 0;
  (charges || []).forEach((c) => {
    const v = Number(c?.value) || 0;
    const sign = c?.option === 'Debit' ? -1 : 1;
    if (c?.type === 'Percentage (%)') x += sign * ((base * v) / 100);
    else x += sign * v;
  });
  return roundMoney(x);
}

export function quoteItemTotals(items = []) {
  let base = 0;
  let discount = 0;
  let net = 0;
  activeQuoteItems(items).forEach((it) => {
    base += quoteLineBase(it);
    discount += quoteLineDiscountAmount(it);
    net += quoteLineNetBeforeGst(it);
  });
  return {
    baseSubTotal: roundMoney(base),
    discountTotal: roundMoney(discount),
    subTotal: roundMoney(net),
  };
}

const closeMoney = (a, b) => Math.abs(roundMoney(a) - roundMoney(b)) < 0.02;

export function getQuoteCommercialTotals(quote = {}, overrideItems = null) {
  const items = overrideItems || quote?.items || [];
  const itemTotals = quoteItemTotals(items);
  const allActiveItems = activeQuoteItems(quote?.items || []);
  const hasOverrideItems = overrideItems !== null;
  const isFilteredSubset = hasOverrideItems && overrideItems.length < allActiveItems.length;
  const hasDisabledLines = (quote?.items || []).some((item) => !isQuoteLineEnabled(item));
  const savedDiscount = Math.max(0, roundMoney(quote?.discount));
  const savedTotalRaw = Number(quote?.total);
  const additionalDiscount = Math.max(
    0,
    roundMoney(
      quote?.additionalDiscount ??
        (items || []).find((item) => item?._additionalDiscount !== undefined)?._additionalDiscount
    )
  );
  const hasSavedTotal = Number.isFinite(savedTotalRaw) && savedTotalRaw > 0;

  const totalWithDiscount = (discount) => {
    const beforeCharges = Math.max(0, itemTotals.baseSubTotal - discount - additionalDiscount);
    const chargeAdjustment = quoteChargesAdjustment(quote?.charges || [], beforeCharges);
    return {
      chargeAdjustment,
      total: Math.max(0, roundMoney(beforeCharges + chargeAdjustment)),
    };
  };

  const lineDiscount = itemTotals.discountTotal;
  const effectiveDiscount = Math.max(lineDiscount, savedDiscount);
  const candidate = totalWithDiscount(effectiveDiscount);

  let chargeAdjustment = candidate.chargeAdjustment;
  let total = candidate.total;

  if (hasSavedTotal && !isFilteredSubset) {
    if (closeMoney(savedTotalRaw, candidate.total)) {
      total = roundMoney(savedTotalRaw);
    }
  }

  return {
    ...itemTotals,
    lineDiscountTotal: lineDiscount,
    discountTotal: roundMoney(effectiveDiscount),
    additionalDiscount,
    chargeAdjustment,
    total: roundMoney(total),
  };
}
